import { useCountry, useCryptoCurrency, useNet } from '@acme/domains';
import { Form, FormControls, Separator, useFormModel } from '@acme/foundation';
import { Button } from 'flowbite-react';
import { useMemo } from 'react';
import {
  ADD_SECTION_IDS,
  MAIN_SECTION_IDS,
  formAgentInfoFormConfig,
  type FormAgentInfoFormData,
} from './model';

export type { FormAgentInfoFormData };

export type FormAgentInfoModalProps = {
  onConfirm: (data: FormAgentInfoFormData) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  inline?: boolean;
};

const initialCtx: FormAgentInfoFormData = {
  commissionAuthorizedOrganizationRUB: '',
  commissionAgentRUB: '',
  agentCryptoAddress: '',
  agentWalletNetId: '',
  cryptoCurrencyId: '',
  cryptoAmount: '',
  organizationName: '',
  senderCountryId: '',
  bankName: '',
  files: [],
};

export const ApplicationFormAgentInfo = ({
  onConfirm,
  onCancel,
  isSubmitting = false,
  inline = false,
}: FormAgentInfoModalProps) => {
  const { currencies } = useCryptoCurrency();
  const { nets } = useNet();
  const { countries } = useCountry();

  const config = useMemo(() => {
    return formAgentInfoFormConfig({
      nets: nets.map((i) => ({
        label: i.name ?? '',
        value: String(i.id ?? ''),
      })),
      cryptoCurrencies: currencies.map((i) => ({
        label: i.name ?? '',
        value: String(i.id ?? ''),
      })),
      countries: countries.map((i) => ({
        label: i.name ?? '',
        value: String(i.id ?? ''),
      })),
    });
  }, [currencies, nets, countries]);

  const form = useFormModel<FormAgentInfoFormData>({ config, ctx: initialCtx });

  const values = form.state.values as FormAgentInfoFormData;
  const hasFiles = (values.files?.length ?? 0) > 0;
  const canSubmit = !isSubmitting && !form.state.isSubmitting && hasFiles;

  return (
    <Form<FormAgentInfoFormData>
      form={form}
      onSubmit={onConfirm}
      className="space-y-6"
    >
      <h2 className="text-xl font-semibold text-gray-900">
        Предложение о сделке
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormControls
          form={form}
          filter={(cfg) =>
            cfg.id != null &&
            (MAIN_SECTION_IDS as readonly string[]).includes(cfg.id)
          }
        />
      </div>

      <Separator className="-mx-6" />

      <h2 className="text-xl font-semibold text-gray-900">
        Данные о плательщике агента
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <FormControls
          form={form}
          filter={(cfg) =>
            cfg.id != null &&
            (ADD_SECTION_IDS as readonly string[]).includes(cfg.id)
          }
        />
      </div>

      <Separator className="-mx-6" />

      <div className="space-y-2">
        <FormControls form={form} filter={(cfg) => cfg.id === 'files'} />
      </div>

      <Separator className="-mx-6" />

      <div className="flex justify-end">
        <Button type="submit" color="primary" disabled={!canSubmit}>
          {isSubmitting || form.state.isSubmitting
            ? 'Отправка...'
            : 'Отправить предложение'}
        </Button>
        {!inline && onCancel ? (
          <Button
            type="button"
            color="light"
            onClick={onCancel}
            className="ml-2"
          >
            Отмена
          </Button>
        ) : null}
      </div>
    </Form>
  );
};
